import React, { useState, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';
import GestureControl from './components/GestureControl';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import Research from './pages/Research';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import GestureIndicator from './components/GestureIndicator';
import GestureStatus from './components/GestureStatus';
import Scene3D from './components/Scene3D';
import ParticlesBackground from './components/ParticlesBackground';
import { GestureProvider, useGesture } from './context/GestureContext';

// Admin Imports
import ProtectedRoute from './components/ProtectedRoute';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminProjects from './pages/AdminProjects';
import AdminResearch from './pages/AdminResearch';
import AdminContact from './pages/AdminContact';
import AdminSubscribers from './pages/AdminSubscribers';
import Footer from './components/Footer';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentGesture, setCurrentGesture] = useState('');
  const { emitGesture, gesturesEnabled, setGesturesEnabled, cardNavActive, setCardNavActive } = useGesture();

  const pages = ['/', '/research', '/projects', '/contact'];

  const scrollPage = (amount) => {
    window.scrollBy({ top: amount, behavior: 'smooth' });
  };

  const lastPointTime = useRef(0);

  const handleGesture = (gesture, pos) => {
    setCurrentGesture(gesture);
    emitGesture(gesture, pos);

    const currentIndex = pages.indexOf(location.pathname);
    const isCardPage = location.pathname === '/projects' || location.pathname === '/research';

    switch (gesture) {
      case 'swipe_right':
        // Navigation ONLY works if Card Mode is DISABLED
        if (!cardNavActive || !isCardPage) {
          if (currentIndex > 0) navigate(pages[currentIndex - 1]);
        }
        break;
      case 'swipe_left':
        if (!cardNavActive || !isCardPage) {
          if (currentIndex < pages.length - 1) navigate(pages[currentIndex + 1]);
        }
        break;
      case 'thumbs_up':
        scrollPage(-500);
        break;
      case 'thumbs_down':
        scrollPage(500);
        break;
      case 'point':
        if (isCardPage) {
          const now = Date.now();
          if (now - lastPointTime.current >= 1500) {
            setCardNavActive(!cardNavActive);
            lastPointTime.current = now;
          }
        }
        break;
      case 'peace':
        navigate('/');
        break;
      case 'fist_hold':
        setGesturesEnabled(false);
        break;
      case 'rock_on':
        // Handled in sub-pages for specific actions
        break;
      default:
        break;
    }
  };

  return (
    <div className="App">
      <Suspense fallback={<div className="loading-screen">Loading 3D Scene...</div>}>
        <Scene3D />
      </Suspense>
      <ParticlesBackground />

      <Navigation />
      <GestureControl
        onGesture={handleGesture}
        enabled={gesturesEnabled}
        onEnabledChange={setGesturesEnabled}
      />
      <GestureIndicator gesture={currentGesture} enabled={gesturesEnabled} />
      <GestureStatus />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Home />
            </motion.div>
          } />
          <Route path="/research" element={
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Research />
            </motion.div>
          } />
          <Route path="/projects" element={
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Projects />
            </motion.div>
          } />
          <Route path="/contact" element={
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Contact />
            </motion.div>
          } />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/projects" element={
            <ProtectedRoute>
              <AdminProjects />
            </ProtectedRoute>
          } />
          <Route path="/admin/research" element={
            <ProtectedRoute>
              <AdminResearch />
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedRoute>
              <AdminContact />
            </ProtectedRoute>
          } />
          <Route path="/admin/subscribers" element={
            <ProtectedRoute>
              <AdminSubscribers />
            </ProtectedRoute>
          } />
        </Routes>
      </AnimatePresence>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <GestureProvider>
        <AppContent />
      </GestureProvider>
    </Router>
  );
}

export default App;
